import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  DragDropContext,
  Droppable,
  Draggable
} from 'react-beautiful-dnd';
import {
  Button,
  Card,
  Icon,
  IconButton,
  TextField,
  Typography
} from '@material-ui/core';
import {
  BackdropCircular,
  Breadcrumb,
  DesignedInfoDialog
} from 'components';
import {
  getQuoteProGroupsAttributeApi,
  updateQuoteProGroupsAttributeApi
} from 'api';
import { isArraysNotEqual } from 'helpers/other';
import './styles.css';


const QuoteGroupsConfigurations = () => {
  const dispatch = useDispatch();

  const [sortableGroups, setSortableGroups] = useState([]);
  const [isGroupsUpdated, toggleGroupsUpdates] = useState(false);
  const [groupsToDelete, setGroupsToDelete] = useState([]);
  const [editGroup, setEditGroup] = useState({isOpenModal: false, groupIndex: null, label: ''});
  const [addGroup, setAddGroup] = useState({isOpenModal: false, label: ''});

  const {
    quoteGroupsAttributeData,
    quoteGroupsLoading
  } = useSelector((state: IReduxState) => state.quote);

  useEffect(() => {
    getQuoteProGroupsAttributeApi({dispatch});
  }, []);

  useEffect(() => {
    toggleGroupsUpdates(isArraysNotEqual(groups, sortableGroups));
  }, [sortableGroups]);

  const groups = quoteGroupsAttributeData?.options
    .filter((group) => !!group.value.length) || [];

  useEffect(() => {
    setSortableGroups(groups);
  }, [quoteGroupsAttributeData]);

  const handleOnDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    setSortableGroups((sortableGroups) => {
      const [reorderedItem] = sortableGroups.splice(result.source.index, 1);
      sortableGroups.splice(result.destination.index, 0, reorderedItem);
      return sortableGroups;
    });
    toggleGroupsUpdates(isArraysNotEqual(groups, sortableGroups));
  };

  const handleDeleteGroup = (index) => {
    const groupValueToDelete = sortableGroups[index].value;

    if (
      !groupValueToDelete.includes('i') &&
      !groupsToDelete.includes(groupValueToDelete)
    ) {
      setGroupsToDelete((groups) => [...groups, groupValueToDelete]);
    }

    setSortableGroups((sortableGroups) => sortableGroups.filter((g, i) => i !== index));
  };

  const handleAddGroup = () => {
    setSortableGroups((sortableGroups) => [
      ...sortableGroups,
      {
        label: addGroup.label,
        value: `${new Date().getTime()}i` //add fake Id, for draggableId props
      }
    ]);
    setAddGroup({isOpenModal: false, label: ''});
  };

  const handleEditGroup = (index) => {
    const editGroup = {
      isOpenModal: true,
      groupIndex: index,
      label: sortableGroups[index].label
    };
    setEditGroup(() => editGroup);
  };

  const handleSaveEdits = () => {
    const { groupIndex, label } = editGroup;
    setSortableGroups((sortableGroups) => sortableGroups
      .map((group, index) => index === groupIndex ? {...group, label} : group)
    );
    setEditGroup({isOpenModal: false, groupIndex: null, label: ''});
  };

  const handleCancelChanges = () => {
    setSortableGroups(groups);
    setGroupsToDelete([]);
  };

  const handleSaveGroups = () => {
    const groups = sortableGroups.map((group) => group.value.includes('i')
      ? {label: group.label}
      : group
    ).filter((group) => group.value !== 'All');
    const attribute = {...quoteGroupsAttributeData, options: groups};
    updateQuoteProGroupsAttributeApi({dispatch, attribute, groupsToDelete});
    setGroupsToDelete([]);
  };

  return (
    <div className="flex-grow h-full min-h-full-screen scroll-y pb-25">
      <div className="mb-sm-30">
        <Breadcrumb
          routeSegments={[
            { name: 'Configure Groups' }
          ]}
        />
      </div>
      <Card className="mx-10 p-8 root-card">
        <Typography variant="h4">
          Groups Preview
        </Typography>
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId="groups">
            {(provided) => (
              <ul className="sortable-list" {...provided.droppableProps} ref={provided.innerRef}>
                {sortableGroups.filter((group) => group.value !== 'All').map(({value, label}, index) => {
                  return (
                    <Draggable key={value} draggableId={value} index={index}>
                      {(provided) => (
                        <li
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          <div
                            className="marker"
                            {...provided.dragHandleProps}
                          >
                            <Icon>drag_indicator</Icon>
                          </div>
                          <div className="p-5 content">{label}</div>
                          <div className="actions">
                            <IconButton
                              size="medium"
                              onClick={() => handleEditGroup(index)}
                            >
                              <Icon fontSize="small">edit</Icon>
                            </IconButton>
                            <IconButton
                              size="medium"
                              onClick={() => handleDeleteGroup(index)}
                            >
                              <Icon fontSize="small">delete</Icon>
                            </IconButton>
                          </div>
                        </li>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
        <div className="buttons-container">
          <Button
            color="primary"
            variant="outlined"
            onClick={() => setAddGroup((addGroup) => ({...addGroup, isOpenModal: true}))}
          >
            <Icon className="mr-2">add</Icon>
            Add Group
          </Button>
          {isGroupsUpdated &&
            <div>
              <Button
                variant="outlined"
                onClick={handleCancelChanges}
                className="mr-4"
              >
                Cancel
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={handleSaveGroups}
              >
                Confirm Changes
              </Button>
            </div>
          }
        </div>
        <DesignedInfoDialog
          open={addGroup.isOpenModal}
          setOpen={() => setAddGroup((addGroup) => ({...addGroup, isOpenModal: false}))}
        >
          <Typography variant="h5" color="primary" className="uppercase">
            Create Group Name
          </Typography>
          <TextField
            required
            fullWidth
            variant="outlined"
            name="label"
            placeholder="Type Group Name"
            className="max-w-400 my-6"
            value={addGroup.label}
            onChange={({target: {value}}) => setAddGroup(
              (addGroup) => ({...addGroup, label: value})
            )}
          />
          <div>
            <Button
              color="primary"
              variant="contained"
              disabled={!addGroup.label.length}
              className="mb-4"
              onClick={handleAddGroup}
            >
              Create Group
            </Button>
          </div>
        </DesignedInfoDialog>
        <DesignedInfoDialog
          open={editGroup.isOpenModal}
          setOpen={() => setEditGroup((editGroup) => ({...editGroup, isOpenModal: false}))}
        >
          <Typography variant="h5" color="primary" className="uppercase">
            Edit Group Name
          </Typography>
          <TextField
            required
            fullWidth
            variant="outlined"
            name="label"
            placeholder="Type Group Name"
            className="max-w-400 my-6"
            value={editGroup.label}
            onChange={({target: {value}}) => setEditGroup(
              (editGroup) => ({...editGroup, label: value})
            )}
          />
          <div>
            <Button
              color="primary"
              variant="contained"
              disabled={!editGroup.label.length}
              className="mb-4"
              onClick={handleSaveEdits}
            >
              Save
            </Button>
          </div>
        </DesignedInfoDialog>
        <BackdropCircular loading={quoteGroupsLoading} />
      </Card>
    </div>
  );
};

export default QuoteGroupsConfigurations;
